import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShoppingBag } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useStock } from "@/context/StockContext";
import { PRODUCTS } from "@/data/products";
import { useCatalog, isHiddenById } from "@/context/CatalogContext";
import ProductCard from "@/components/store/ProductCard";
import { trackEvent } from "@/lib/analytics";

/**
 * Homepage preview of the BOB Store.
 * Sits right under the "Shop by Country" section and gives visitors an
 * immediate taste of what's in stock — without leaving the landing page.
 *
 * Strategy for the curated selection:
 *  1) Surface tagged products first (e.g. "new", "limited").
 *  2) Fill remaining slots with the first available in-stock products.
 *  3) Always render the same number of cards (8) for a balanced grid.
 */
const FEATURED_COUNT = 8;

export default function StorePreview() {
  const { t, isRTL } = useLanguage();
  const S = t.store;
  const { isProductOutOfStock } = useStock();
  const { version: catalogVersion } = useCatalog();

  const featured = useMemo(() => {
    const visible = PRODUCTS.filter((p) => !isHiddenById(p.id));
    const inStock = visible.filter((p) => !isProductOutOfStock(p.id));
    const tagged = inStock.filter((p) => (p.tags || []).length > 0);
    const rest = inStock.filter((p) => !(p.tags || []).length);
    return [...tagged, ...rest].slice(0, FEATURED_COUNT);
  }, [isProductOutOfStock, catalogVersion]);

  if (featured.length === 0) return null;

  return (
    <section
      id="store-preview"
      data-testid="store-preview-section"
      className="border-t border-black/5 bg-[linear-gradient(180deg,#ffffff_0%,#fbfbfc_100%)] px-5 py-20 md:px-8 md:py-28 dark:border-white/10 dark:bg-[linear-gradient(180deg,#080808_0%,#101010_100%)]"
    >
      <div className="mx-auto max-w-7xl">
        {/* Header — eyebrow + title + view-all CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="mb-10 flex flex-col gap-6 md:mb-14 md:flex-row md:items-end md:justify-between md:gap-10"
        >
          <div className="max-w-2xl">
            <div
              className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#F37021]/25 bg-[#FDF1E8] px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wide text-[#C55810] dark:bg-[#1A1A1A] dark:text-[#F8A056]"
              data-testid="store-preview-eyebrow"
            >
              <ShoppingBag className="w-3.5 h-3.5" />
              {S.previewEyebrow}
            </div>
            <h2
              className="font-display text-4xl font-bold leading-[1.08] text-[#111] sm:text-5xl md:text-6xl dark:text-white"
              data-testid="store-preview-title"
            >
              {S.previewTitle}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[#555] md:text-lg dark:text-[#BAC4D4]">
              {S.previewSubtitle}
            </p>
          </div>

          <Link
            to="/stock"
            onClick={() => {
              trackEvent("bob_store_click", {
                routeName: "home_store_preview",
                metadata: { source: "store_preview_header" },
              });
            }}
            className="group inline-flex h-12 items-center justify-center gap-2 self-start whitespace-nowrap rounded-full bg-[#111] px-6 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-[#F37021] md:self-end dark:bg-[#1B1B1B]"
            data-testid="store-preview-view-all"
          >
            {S.previewViewAll}
            <ArrowRight
              className={`w-4 h-4 transition-transform group-hover:translate-x-1 ${
                isRTL ? "rotate-180 group-hover:-translate-x-1 group-hover:translate-x-0" : ""
              }`}
            />
          </Link>
        </motion.div>

        {/* Product grid — same card component as the full store */}
        <div
          className="grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 md:gap-6 lg:grid-cols-4"
          data-testid="store-preview-grid"
        >
          {featured.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>

        {/* Bottom CTA — secondary route to /stock for mobile users who scroll */}
        <div className="mt-10 flex justify-center md:mt-12">
          <Link
            to="/stock"
            onClick={() => {
              trackEvent("bob_store_click", {
                routeName: "home_store_preview",
                metadata: { source: "store_preview_footer" },
              });
            }}
            className="group inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[#F37021] px-7 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(243,112,33,0.28)] transition-all hover:-translate-y-0.5 hover:bg-[#E06015]"
            data-testid="store-preview-view-all-bottom"
          >
            {S.previewViewAll}
            <ArrowRight
              className={`w-4 h-4 transition-transform group-hover:translate-x-1 ${
                isRTL ? "rotate-180 group-hover:-translate-x-1 group-hover:translate-x-0" : ""
              }`}
            />
          </Link>
        </div>
      </div>
    </section>
  );
}
